import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiMessageSquare } = FiIcons;

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Teléfono',
      content: '610 70 15 17',
      link: 'tel:610701517',
      description: 'Lunes a Viernes: 9:00 - 19:00'
    },
    {
      icon: FiMail,
      title: 'Email',
      content: 'info@autosfersan.com',
      link: 'mailto:info@autosfersan.com',
      description: 'Respuesta en menos de 24h'
    },
    {
      icon: FiMapPin,
      title: 'Dirección',
      content: 'Calle Isaac Peral, 31',
      link: 'https://maps.google.com/?q=Calle+Isaac+Peral+31+Cáceres',
      description: 'Polígono La Mejostilla, 10004 Cáceres'
    },
    {
      icon: FiMessageSquare,
      title: 'WhatsApp',
      content: 'Contacto Directo',
      link: 'https://wa.me/34610701517',
      description: 'Respuesta inmediata'
    }
  ];

  const businessHours = [
    { day: 'Lunes - Viernes', hours: '9:00 - 14:00 | 16:00 - 19:00' },
    { day: 'Sábados', hours: '9:00 - 14:00' },
    { day: 'Domingos', hours: 'Cerrado' }
  ];

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Mensaje enviado correctamente. Te contactaremos pronto.');
    reset();
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#005c91] to-[#3498db] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Contacta con Nosotros
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 max-w-3xl mx-auto">
              Estamos aquí para ayudarte a encontrar tu vehículo ideal. 
              Contáctanos y te asesoraremos sin compromiso.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-[#2c3e50] mb-8">
                Información de Contacto
              </h2>
              
              <div className="space-y-6 mb-12">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-start space-x-4 p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
                  >
                    <div className="w-12 h-12 bg-[#005c91] rounded-full flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={info.icon} className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2c3e50] mb-1">{info.title}</h3>
                      <a 
                        href={info.link}
                        target={info.link.startsWith('http') ? '_blank' : undefined}
                        rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="text-[#005c91] hover:text-[#3498db] font-medium transition-colors"
                      >
                        {info.content}
                      </a>
                      <p className="text-gray-600 text-sm mt-1">{info.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Business Hours */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="bg-white rounded-lg shadow-md p-8"
              >
                <div className="flex items-center space-x-3 mb-6">
                  <SafeIcon icon={FiClock} className="w-6 h-6 text-[#005c91]" />
                  <h3 className="text-xl font-semibold text-[#2c3e50]">Horario de Atención</h3>
                </div>
                <div className="space-y-3">
                  {businessHours.map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                      <span className="font-medium text-gray-700">{schedule.day}</span>
                      <span className="text-[#005c91] font-semibold">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Contact Form */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-lg shadow-lg p-8"
            >
              <h2 className="text-3xl font-bold text-[#2c3e50] mb-8">
                Envíanos un Mensaje
              </h2>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre *
                    </label>
                    <input
                      {...register('name', { required: 'El nombre es obligatorio' })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent transition-colors"
                      placeholder="Tu nombre completo"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <input
                      {...register('phone', { required: 'El teléfono es obligatorio' })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent transition-colors"
                      placeholder="Tu número de teléfono"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    {...register('email', { 
                      required: 'El email es obligatorio',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Email no válido'
                      }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent transition-colors"
                    placeholder="tu@email.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Asunto
                  </label>
                  <select
                    {...register('subject')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent transition-colors"
                  >
                    <option value="">Selecciona un asunto</option>
                    <option value="informacion-vehiculo">Información sobre vehículo</option>
                    <option value="cita-visita">Solicitar cita/visita</option>
                    <option value="financiacion">Consulta sobre financiación</option>
                    <option value="tasacion">Tasación de vehículo</option>
                    <option value="servicio-postventa">Servicio post-venta</option>
                    <option value="otros">Otros</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    {...register('message', { required: 'El mensaje es obligatorio' })}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent transition-colors"
                    placeholder="Cuéntanos en qué podemos ayudarte..."
                  />
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                  )}
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    {...register('privacy', { required: 'Debes aceptar la política de privacidad' })}
                    className="mt-1 w-4 h-4 text-[#005c91] border-gray-300 rounded focus:ring-[#005c91]"
                  />
                  <label className="text-sm text-gray-600">
                    Acepto la{' '}
                    <a href="/privacidad" className="text-[#005c91] hover:text-[#3498db] underline">
                      política de privacidad
                    </a>{' '}
                    y el tratamiento de mis datos personales. *
                  </label>
                </div>
                {errors.privacy && (
                  <p className="text-red-500 text-sm">{errors.privacy.message}</p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#005c91] hover:bg-[#3498db] disabled:bg-gray-400 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSend} className="w-5 h-5" />
                      <span>Enviar Mensaje</span>
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>

        {/* Map Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mt-16"
        >
          <h2 className="text-3xl font-bold text-[#2c3e50] mb-8 text-center">
            Nuestra Ubicación
          </h2>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3048.8740!2d-6.3729!3d39.4695!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDI4JzEwLjIiTiA2wrAyMicyMi40Ilc!5e0!3m2!1ses!2ses!4v1625000000000!5m2!1ses!2ses"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="AUTOSFERSAN Ubicación"
                className="w-full h-96"
              ></iframe>
            </div>
            <div className="p-6 bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#2c3e50] mb-1">
                    AUTOSFERSAN
                  </h3>
                  <p className="text-gray-600">
                    Calle Isaac Peral, 31 - Polígono La Mejostilla, 10004 Cáceres
                  </p>
                </div>
                <a
                  href="https://maps.google.com/?q=Calle+Isaac+Peral+31+Cáceres"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#005c91] hover:bg-[#3498db] text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                >
                  Ver en Google Maps
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Contact Actions */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mt-16 bg-[#005c91] rounded-lg p-8 text-white text-center"
        >
          <h2 className="text-3xl font-bold mb-4">
            ¿Necesitas Ayuda Inmediata?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Contáctanos directamente para recibir atención personalizada
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:610701517"
              className="bg-[#e67e22] hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors inline-flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>Llamar Ahora</span>
            </a>
            <a
              href="https://wa.me/34610701517"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25d366] hover:bg-green-600 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors inline-flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiMessageSquare} className="w-5 h-5" />
              <span>WhatsApp</span>
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage;